import { DashboardCard } from "@/components/ui/DashboardCard/DashboardCard";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table/Table";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type PaginationState,
  type SortingState,
} from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/Badge/Badge";
import { HStack } from "@/components/layout/HStack";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu/DropdownMenu";
import { Button } from "@/components/ui/Button/Button";
import { ArrowDown, ArrowUp, ArrowUpDown, Eye, Filter } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select/Select";
import { RESULTS_PER_PAGE_OPTIONS } from "@/constants/tableOptions";
import {
  Pagination,
  PaginationButton,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/Pagination/Pagination";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/Tooltip/Tooltip";
import { useSearchParams } from "react-router-dom";
import { parseCsv, writeCsv } from "@/utils/csv";
import { Input } from "@/components/ui/Input/Input";
import type { ApiErrorLog } from "@shared/types/models";
import type { BadgeVariant } from "@/components/ui/Badge/Badge";

const statusBadgeVariant = (status?: number): BadgeVariant => {
  if (!status) return "outline";
  if (status >= 500) return "destructive";
  if (status >= 400) return "secondary";
  return "outline";
};

const statusBucketLabel: Record<string, string> = {
  "5xx": "5xx Server",
  "4xx": "4xx Client",
  other: "Other",
};

const STATUS_BUCKETS = ["5xx", "4xx", "other"] as const;
type StatusBucket = (typeof STATUS_BUCKETS)[number];

const bucketOf = (status?: number): StatusBucket => {
  if (status && status >= 500) return "5xx";
  if (status && status >= 400) return "4xx";
  return "other";
};

const formatTimestamp = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString();
};

const truncate = (s: string | undefined, max: number) => {
  if (!s) return "—";
  return s.length > max ? s.slice(0, max - 1) + "…" : s;
};

const getColumns = ({
  onView,
}: {
  onView: (id: string) => void;
}): ColumnDef<ApiErrorLog>[] => [
  {
    accessorKey: "createdAt",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Time{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDown />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUp />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    cell: ({ row }) => (
      <span style={{ whiteSpace: "nowrap" }}>
        {formatTimestamp(row.original.createdAt)}
      </span>
    ),
    sortingFn: (a, b) =>
      new Date(a.original.createdAt).getTime() -
      new Date(b.original.createdAt).getTime(),
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => (
      <Badge variant={statusBadgeVariant(row.original.status)}>
        {row.original.status ?? "—"}
      </Badge>
    ),
    filterFn: (row, _columnId, value: StatusBucket[] | undefined) => {
      if (!value || value.length === 0) return true;
      return value.includes(bucketOf(row.original.status));
    },
  },
  {
    accessorKey: "method",
    header: "Method",
    cell: ({ row }) => row.original.method ?? "—",
  },
  {
    accessorKey: "path",
    header: "Path",
    cell: ({ row }) => (
      <span
        style={{
          fontFamily: "var(--font-mono, monospace)",
          fontSize: "var(--text-sm)",
        }}
      >
        {truncate(row.original.path, 60)}
      </span>
    ),
  },
  {
    accessorKey: "message",
    header: "Message",
    cell: ({ row }) => truncate(row.original.message, 80),
    filterFn: (row, _columnId, value: string) => {
      if (!value) return true;
      const v = value.toLowerCase();
      return (
        (row.original.message?.toLowerCase().includes(v) ?? false) ||
        (row.original.path?.toLowerCase().includes(v) ?? false)
      );
    },
  },
  {
    id: "user",
    header: "User",
    cell: ({ row }) => row.original.user?.name ?? "—",
  },
  {
    id: "actions",
    header: () => (
      <div style={{ textAlign: "right", fontWeight: "var(--font-medium)" }}>
        Actions
      </div>
    ),
    cell: ({ row }) => (
      <HStack justify="flex-end" gap="0.25rem">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon-small"
              variant="outline"
              onClick={() => onView(row.original._id)}
            >
              <Eye />
            </Button>
          </TooltipTrigger>
          <TooltipContent>View details</TooltipContent>
        </Tooltip>
      </HStack>
    ),
  },
];

const DataTable = ({
  data,
  columns,
}: {
  data: ApiErrorLog[];
  columns: ColumnDef<ApiErrorLog>[];
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const sortKRaw = searchParams.get("sortK");
  const sortVRaw = searchParams.get("sortV");

  const urlSorting = useMemo<SortingState>(() => {
    const validK = sortKRaw === "createdAt";
    const validV = sortVRaw === "asc" || sortVRaw === "desc";
    if (!validK || !validV) {
      return [{ id: "createdAt", desc: true }];
    }
    return [{ id: sortKRaw, desc: sortVRaw === "desc" }];
  }, [sortKRaw, sortVRaw]);

  const onSortingChange = (
    updater: SortingState | ((old: SortingState) => SortingState),
  ) => {
    const next =
      typeof updater === "function" ? updater(urlSorting) : updater;

    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      const first = next[0];
      if (!first) {
        sp.delete("sortK");
        sp.delete("sortV");
        return sp;
      }
      sp.set("sortK", first.id);
      sp.set("sortV", first.desc ? "desc" : "asc");
      return sp;
    });
  };

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onPaginationChange: setPagination,
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      sorting: urlSorting,
      columnFilters,
      pagination,
    },
  });

  const urlBuckets = useMemo(
    () =>
      parseCsv(searchParams.get("status")).filter((b): b is StatusBucket =>
        (STATUS_BUCKETS as readonly string[]).includes(b),
      ),
    [searchParams],
  );

  const availableBuckets = useMemo(() => {
    const set = new Set<StatusBucket>();
    for (const r of data) set.add(bucketOf(r.status));
    return STATUS_BUCKETS.filter((b) => set.has(b));
  }, [data]);

  const toggleBucket = (b: StatusBucket) => {
    const next = urlBuckets.includes(b)
      ? urlBuckets.filter((x) => x !== b)
      : [...urlBuckets, b];
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      writeCsv(sp, "status", next);
      return sp;
    });
  };

  const clearBucketFilter = () => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.delete("status");
      return sp;
    });
  };

  const search = searchParams.get("q") ?? "";
  const setSearch = (v: string) => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      if (v) sp.set("q", v);
      else sp.delete("q");
      return sp;
    });
  };

  useEffect(() => {
    table.getColumn("status")?.setFilterValue(urlBuckets);
    table.getColumn("message")?.setFilterValue(search);
    table.setPageIndex(0);
  }, [table, urlBuckets, search]);

  const totalCount = table.getCoreRowModel().rows.length;
  const filteredCount = table.getFilteredRowModel().rows.length;

  const showPagination = table.getPageCount() > 1;

  function getPageItems(current: number, total: number) {
    const c = current + 1;
    const t = total;
    if (t <= 7) return Array.from({ length: t }, (_, i) => i + 1);
    const items: (number | "…")[] = [1];
    if (c > 3) items.push("…");
    const start = Math.max(2, c - 1);
    const end = Math.min(t - 1, c + 1);
    for (let p = start; p <= end; p++) items.push(p);
    if (c < t - 2) items.push("…");
    items.push(t);
    return items;
  }

  return (
    <>
      <HStack align="center" justify="space-between" wrap>
        <HStack align="center">
          <Input
            placeholder="Search message or path"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: "240px" }}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                style={{
                  height: "fit-content",
                  padding: "var(--space-xxs) var(--space-xs)",
                }}
              >
                <Filter /> Filter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>Status</DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {availableBuckets.map((b) => (
                    <DropdownMenuCheckboxItem
                      key={b}
                      checked={urlBuckets.includes(b)}
                      onCheckedChange={() => toggleBucket(b)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {statusBucketLabel[b]}
                    </DropdownMenuCheckboxItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      clearBucketFilter();
                    }}
                    disabled={urlBuckets.length === 0}
                  >
                    Clear
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            </DropdownMenuContent>
          </DropdownMenu>
          <p style={{ color: "var(--muted-foreground)" }}>
            {filteredCount} of {totalCount} errors
          </p>
        </HStack>
        <Select
          value={String(table.getState().pagination.pageSize)}
          onValueChange={(v) => {
            table.setPageSize(Number(v));
            table.setPageIndex(0);
          }}
        >
          <SelectTrigger
            style={{
              width: "fit-content",
              height: "fit-content",
              padding: "var(--space-xxs) var(--space-xs)",
            }}
          >
            <SelectValue placeholder="Select results per page" />
          </SelectTrigger>
          <SelectContent>
            {RESULTS_PER_PAGE_OPTIONS.map((option) => (
              <SelectItem key={option} value={String(option)}>
                {option} results/page
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </HStack>
      <Table borderColor="var(--secondary-border)">
        <TableHeader>
          {table.getHeaderGroups().map((h) => (
            <TableRow key={h.id}>
              {h.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length}>No records</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {showPagination && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={(e) => {
                  e.preventDefault();
                  table.previousPage();
                }}
                disabled={!table.getCanPreviousPage()}
              />
            </PaginationItem>
            {getPageItems(pagination.pageIndex, table.getPageCount()).map(
              (it, idx) =>
                it === "…" ? (
                  <PaginationItem key={`el-${idx}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                ) : (
                  <PaginationItem key={it}>
                    <PaginationButton
                      isActive={it === pagination.pageIndex + 1}
                      onClick={(e) => {
                        e.preventDefault();
                        table.setPageIndex(it - 1);
                      }}
                    >
                      {it}
                    </PaginationButton>
                  </PaginationItem>
                ),
            )}
            <PaginationItem>
              <PaginationNext
                onClick={(e) => {
                  e.preventDefault();
                  table.nextPage();
                }}
                disabled={!table.getCanNextPage()}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </>
  );
};

const ErrorLogsTable = ({
  data,
  onView,
}: {
  data: ApiErrorLog[];
  onView: (id: string) => void;
}) => {
  const columns = useMemo(() => getColumns({ onView }), [onView]);

  return (
    <DashboardCard>
      <DataTable data={data} columns={columns} />
    </DashboardCard>
  );
};

export { ErrorLogsTable };
